import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { brandColors } from '@/app/constants';
import CloudIcon from '@/component-library/primitives/Icons/CloudIcon/CloudIcon';
import CompanyAccountIcon from '@/component-library/primitives/Icons/CompanyAccountIcon/CompanyAccountIcon';
import TeamMembersIcon from '@/component-library/primitives/Icons/TeamMembersIcon/TeamMembersIcon';
import UserCircleIcon from '@/component-library/primitives/Icons/UserCircleIcon/UserCircleIcon';
import { HttpMethodsEnum, PermissionsEnum } from '@/modules/shared/enums/enums';
import usePermissionChecker from '@/modules/shared/hooks/usePermissionChecker';
import { AppRoutes } from '@/utils/routes/router';

import { buttonClass, isActiveButtonClass } from '../Audience/Audience';

import SettingsCard from './SettingsCard';
import SettingsSubscriptionPanel from './SettingsSubscriptionPanel';
import SettingsUsagePanel from './SettingsUsagePanel';

type ViewModes = 'general' | 'subscription';

const Settings = () => {
  const defaultViewModes: { id: ViewModes; name: string }[] = [
    { id: 'general', name: 'General Settings' },
    { id: 'subscription', name: 'Subscription plans' },
  ];
  const [viewMode, setViewMode] = useState<ViewModes>(defaultViewModes[0].id);

  const { t } = useTranslation();
  // const { data } = useGetAccountMetrics();
  // const accountMetricsData = data ? data : dummyAccountMetrics;
  const isActionAllowed = usePermissionChecker(PermissionsEnum.TEAM_MEMBERS, HttpMethodsEnum.GET);

  return (
    <div className="flex flex-col h-full relative">
      {/* View mode toggle */}
      <div className="sticky z-10 top-0 pt-4 flex items-end mb-6 bg-white after:absolute after:left-[-2rem] after:bottom-[-1px] after:w-[calc(100%+4rem)] after:border-b after:border-custom-lightSilver before:absolute before:left-[-2rem] before:-top-4 before:w-[calc(100%+4rem)] before:border-b before:border-custom-lightSilver before:h-4 before:bg-white">
        {defaultViewModes.map(({ id, name }) => (
          <button
            key={id}
            className={`${buttonClass} ${id === viewMode && isActiveButtonClass}`}
            onClick={() => setViewMode(id)}
          >
            {name}
          </button>
        ))}
      </div>

      {viewMode === 'general' && (
        <React.Fragment>
          <h1 className="text-custom-black w-full font-medium text-xl mb-2">{t('SettingsPage.title')}</h1>
          <p className="w-full text-sm text-custom-darkSilver mb-8">{t('SettingsPage.subtitle')}</p>

          <SettingsUsagePanel />

          <div className="grid grid-cols-3 gap-4 mb-8">
            {isActionAllowed && (
              <SettingsCard
                label={'SettingsPage.teamMembersOptionLabel'}
                description={'SettingsPage.teamMembersOptionDescription'}
                route={AppRoutes.teamMembers}
                icon={<TeamMembersIcon color={brandColors.blue} />}
              />
            )}

            <SettingsCard
              label={'SettingsPage.accountOptionLabel'}
              description={'SettingsPage.accountOptionDescription'}
              route={AppRoutes.account}
              icon={<CompanyAccountIcon color={brandColors.blue} />}
            />

            <SettingsCard
              label={'SettingsPage.personalAccountSettingsOptionLabel'}
              description={'SettingsPage.personalAccountSettingsOptionDescription'}
              route={AppRoutes.personalAccount}
              icon={<UserCircleIcon color={brandColors.blue} />}
            />
            <SettingsCard
              label={'SettingsPage.apiCredentialsOptionLabel'}
              description={'SettingsPage.apiCredentialsOptionDescription'}
              route={AppRoutes.apiCredentials}
              icon={<CloudIcon />}
            />
          </div>
        </React.Fragment>
      )}

      {viewMode === 'subscription' && (
        <React.Fragment>
          <SettingsSubscriptionPanel />
        </React.Fragment>
      )}

      {/* <div>
        <p className="text-grayDark text-sm mb-8">{t('SettingsPage.subtitle')}</p>
      </div>

      <div>
        <section className="px-6 pt-6 pb-8 shadow rounded-[10px]  bg-primary-0 text-custom-aliceBlue">
          <h2 className="text-xs font-semibold text-custom-textBlack uppercase mb-6">
            {t('SettingsPage.accountSummaryLabel')}
          </h2>
          <div className="space-y-6">
            <div className="flex flex-row gap-8">
              <div className="flex flex-row items-center gap-4">
                <div className="w-fit px-2 h-7 bg-numberOrPeopleInSegmentBG rounded-[5px] flex items-center justify-center text-custom-textBlack text-sm">
                  {`${accountMetricsData.currentMemberCount} / ${accountMetricsData.memberCountLimit}`}
                </div>
                <div className="text-gray-400 text-sm">{t('SettingsPage.currentPeopleCountLabel')}</div>
              </div>
              <div className="flex flex-row items-center gap-4">
                <div className="w-fit px-2 h-7 bg-numberOrPeopleInSegmentBG rounded-[5px] flex items-center justify-center text-custom-textBlack text-sm">
                  {`${accountMetricsData.currentObjects} / ${accountMetricsData.objectCountLimit}`}
                </div>
                <div className="text-gray-400 text-sm">{t('SettingsPage.currentObjectsCountLabel')}</div>
              </div>
            </div>
            <div className="flex flex-row gap-8">
              <div className="flex flex-row items-center gap-4">
                <div className="w-fit px-2 h-7 bg-numberOrPeopleInSegmentBG rounded-[5px] flex items-center justify-center text-custom-textBlack text-sm">
                  {`${accountMetricsData.emailsSentSinceSubscriptionStart} / ${accountMetricsData.emailLimit}`}
                </div>
                <div className="flex flex-row items-center gap-2 text-gray-400 text-sm">
                  {t('SettingsPage.emailsSentSinceLabel', {
                    date: accountMetricsData.subscriptionStartDate.toLocaleDateString('en-us', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    }),
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-3 gap-4 mb-8">
          {isActionAllowed && (
            <SettingsCard
              label={'SettingsPage.teamMembersOptionLabel'}
              description={'SettingsPage.teamMembersOptionDescription'}
              route={AppRoutes.teamMembers}
              icon={<TeamMembersIcon />}
            />
          )}
          <SettingsCard
            label={'SettingsPage.accountOptionLabel'}
            description={'SettingsPage.accountOptionDescription'}
            route={AppRoutes.account}
            icon={<CompanyAccountIcon />}
          />
          <SettingsCard
            label={'SettingsPage.pushNotificationsOptionLabel'}
            description={'SettingsPage.pushNotificationsOptionDescription'}
            route={AppRoutes.pushNotifications}
            icon={<BellRingIcon color="#7898FB" width="35" height="35" />}
          />
          <SettingsCard
            label={'SettingsPage.personalAccountSettingsOptionLabel'}
            description={'SettingsPage.personalAccountSettingsOptionDescription'}
            route={AppRoutes.personalAccount}
            icon={<UserCircleIcon />}
          />
          <SettingsCard
            label={'SettingsPage.domainsOptionLabel'}
            description={'SettingsPage.domainsOptionDescription'}
            route={AppRoutes.domains}
            icon={<GlobeIcon />}
          />

          <SettingsCard
            label={'SettingsPage.securityOptionLabel'}
            description={'SettingsPage.securityOptionDescription'}
            icon={<ShieldIcon />}
            disabled={true}
          />
          <SettingsCard
            label={'SettingsPage.plansBillingOptionLabel'}
            description={'SettingsPage.plansBillingOptionDescription'}
            icon={<CreditCardIcon />}
            disabled={true}
          />
          <SettingsCard
            label={'SettingsPage.privacyDataOptionLabel'}
            description={'SettingsPage.privacyDataOptionDescription'}
            icon={<LockIcon />}
            disabled={true}
          />
        </div>
      </div> */}
    </div>
  );
};

export default Settings;
